import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const EFFECT_ATTRIBUTES = {
  "ken-burns": { from: { scale: 1 }, to: { scale: 1.25 } },
  "reversed-ken-burns": { from: { scale: 1.25 }, to: { scale: 1 } },
  "ken-burns-fly-in": { from: { scale: 0.9 }, to: { scale: 1.25 } },
  "parallax": { from: { y: "15%" }, to: { y: "-15%" } },
  "reversed-parallax": { from: { y: "-15%" }, to: { y: "15%" } },
  "x-parallax": { from: { x: "15%" }, to: { x: "-15%" } },
  "x-parallax-soft": { from: { x: "5%" }, to: { x: "-5%" } },
  "reversed-x-parallax": { from: { x: "-15%" }, to: { x: "15%" } },
  "reversed-x-parallax-soft": { from: { x: "-5%" }, to: { x: "5%" } }
}

/**
 * Single Teaser Animation Effects
 */
class TeaserAnimations {
  constructor() {
    this.containers = document.querySelectorAll('.js-effect-container');
    this.animationElements = [];
    this.isInitialized = false;

    if (this.containers.length > 0) {
      this.init();
    }
  }

  init() {
    this.containers.forEach((container, index) => {
      const image = container.querySelector('.js-effect-image');
      const type = container.dataset.effect || 'none';
      const scale = container.dataset.scale || 1.3;

      if (image) {
        this.animationElements.push({
          container: container,
          image: image,
          id: `animation-${index}`,
          tween: null,
          type,
          scale
        });
      }
    });

    this.setupAnimationEffects();
    this.isInitialized = true;
  }

  setupAnimationEffects() {
    this.animationElements.forEach((element) => {
      if (element.type.includes("ken-burns")) {
        this.setupKenBurnsEffect(element);
      } else if (element.type.includes("parallax")) {
        this.setupParallaxEffect(element);
      }
    });
  }

  setupParallaxEffect(element) {
    const { container, image, type, scale } = element;

    element.tween = gsap.fromTo(
      image,
      {
        scale,
        ...EFFECT_ATTRIBUTES[type].from
      },
      {
        ...EFFECT_ATTRIBUTES[type].to,
        ease: 'none',
        scrollTrigger: {
          trigger: container,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
          invalidateOnRefresh: true
        }
      }
    );
  }

  setupKenBurnsEffect(element) {
    const { container, image, type } = element;

    element.tween = gsap.fromTo(
      image,
      {
        ...EFFECT_ATTRIBUTES[type].from,
      },
      {
        ...EFFECT_ATTRIBUTES[type].to,
        ease: 'none',
        scrollTrigger: {
          trigger: container,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
          invalidateOnRefresh: true
        }
      }
    );
  }

  refresh() {
    if (this.isInitialized && window.ScrollTrigger) {
      ScrollTrigger.refresh();
    }
  }

  destroy() {
    this.animationElements.forEach((element) => {
      if (element.tween) {
        element.tween.scrollTrigger?.kill();
        element.tween.kill();
      }
    });

    this.animationElements = [];
    this.isInitialized = false;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Make sure GSAP and ScrollTrigger are loaded
  if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
    window.teaserAnimations = new TeaserAnimations();
  } else {
    console.warn('GSAP or ScrollTrigger not found. Animation effects disabled.');
  }
});

// Refresh on window resize
window.addEventListener('resize', () => {
  if (window.teaserAnimations) {
    window.teaserAnimations.refresh();
  }
});
